// =============================================================
// 檔案名稱：RegionSelector.js
// 版本：V1.01
// 日期：2025/07/19
// 主要功能與職責：
//   區域權限選擇器組件，提供直觀的區域權限選擇介面。
// 這個檔案是做什麼的？
//   提供複選區域權限的選擇器，支援全選/取消全選功能，
//   並提供視覺化的區域標示和權限狀態顯示。
// 解決了什麼問題？
//   讓管理員能方便地為夥伴設定區域權限，提供友善的使用者介面。
// 更新歷史：
// V1.01 - 初版建立
// 注意事項：
//   此組件需要在夥伴管理功能中使用，確保權限驗證。
// 關連檔案：regions.js, regionUtils.js, AdminPanel.js
// =============================================================

import React, { useState, useEffect } from 'react';
import { 
    ALL_REGIONS, 
    REGION_NAMES, 
    REGION_ICONS, 
    REGION_COLORS 
} from '../../constants/regions';
import { isValidRegionsArray } from '../../utils/regionUtils';

const RegionSelector = ({ 
    selectedRegions = [], 
    onChange, 
    disabled = false,
    showSelectAll = true,
    className = ''
}) => {
    const [localSelectedRegions, setLocalSelectedRegions] = useState(selectedRegions);

    // 當外部 selectedRegions 變更時，同步本地狀態
    useEffect(() => {
        if (isValidRegionsArray(selectedRegions)) {
            setLocalSelectedRegions([...selectedRegions]);
        }
    }, [selectedRegions]);

    // 處理區域選擇變更
    const handleRegionToggle = (regionCode) => {
        if (disabled) return;

        const newSelectedRegions = localSelectedRegions.includes(regionCode)
            ? localSelectedRegions.filter(r => r !== regionCode)
            : [...localSelectedRegions, regionCode];

        setLocalSelectedRegions(newSelectedRegions);
        onChange?.(newSelectedRegions);
    };

    // 處理全選/取消全選
    const handleSelectAll = () => {
        if (disabled) return;

        const newSelectedRegions = localSelectedRegions.length === ALL_REGIONS.length
            ? []
            : [...ALL_REGIONS];

        setLocalSelectedRegions(newSelectedRegions);
        onChange?.(newSelectedRegions);
    };

    // 檢查是否全選
    const isAllSelected = localSelectedRegions.length === ALL_REGIONS.length;
    const isPartiallySelected = localSelectedRegions.length > 0 && !isAllSelected;

    return (
        <div className={`region-selector ${className}`}>
            {/* 標題和全選按鈕 */}
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">
                    區域權限設定
                </h4>
                {showSelectAll && (
                    <button
                        type="button"
                        onClick={handleSelectAll}
                        disabled={disabled}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${
                            disabled
                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                : isAllSelected
                                ? 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200'
                                : 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200'
                        }`}
                    >
                        {isAllSelected ? '取消全選' : '全選'}
                    </button>
                )}
            </div>

            {/* 區域選擇器 */}
            <div className="grid grid-cols-2 gap-2">
                {ALL_REGIONS.map(regionCode => {
                    const isSelected = localSelectedRegions.includes(regionCode);
                    const regionName = REGION_NAMES[regionCode];
                    const regionIcon = REGION_ICONS[regionCode];
                    const regionColor = REGION_COLORS[regionCode];

                    return (
                        <button
                            key={regionCode}
                            type="button"
                            onClick={() => handleRegionToggle(regionCode)}
                            disabled={disabled}
                            className={`
                                flex items-center gap-2 p-3 rounded-lg border-2 transition-all
                                ${disabled
                                    ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
                                    : isSelected
                                    ? `bg-${regionColor}-50 border-${regionColor}-300 text-${regionColor}-800 shadow-sm`
                                    : `bg-white border-gray-200 text-gray-700 hover:border-${regionColor}-200 hover:bg-${regionColor}-50`
                                }
                            `}
                        >
                            {/* 選擇狀態指示器 */}
                            <div className={`
                                w-4 h-4 rounded border-2 flex items-center justify-center
                                ${isSelected
                                    ? `bg-${regionColor}-500 border-${regionColor}-500`
                                    : 'border-gray-300'
                                }
                            `}>
                                {isSelected && (
                                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </div>

                            {/* 區域圖示和名稱 */}
                            <span className="text-lg">{regionIcon}</span>
                            <span className="font-medium text-sm">{regionName}</span>
                        </button>
                    );
                })}
            </div>

            {/* 選擇狀態顯示 */}
            <div className="mt-3 text-xs text-gray-500">
                {localSelectedRegions.length === 0 && (
                    <span className="text-red-500">⚠️ 請至少選擇一個區域</span>
                )}
                {localSelectedRegions.length > 0 && (
                    <span>
                        已選擇 {localSelectedRegions.length} 個區域：
                        {localSelectedRegions.map(regionCode => REGION_NAMES[regionCode]).join('、')}
                    </span>
                )}
            </div>

            {/* 權限說明 */}
            <div className="mt-2 text-xs text-gray-400">
                💡 夥伴只能在選定的區域內進行擺攤登記和查看日曆
            </div>
        </div>
    );
};

export default RegionSelector; 